import React, { useCallback, useEffect, useState } from "react";
import {
  Stack,
  Group,
  Select,
  Loader,
  Alert,
  Table,
  Text,
  Avatar,
  ScrollArea,
  Badge,
  Box,
  Button,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconChevronDown,
  IconChevronRight,
  IconRefresh,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import { useToast } from "../../context/ToastContext";

const formatDateRange = (startIso, endIso) => {
  const opts = { month: "short", day: "numeric", timeZone: "UTC" };
  const start = new Date(startIso).toLocaleDateString(undefined, opts);
  const end = new Date(endIso).toLocaleDateString(undefined, opts);
  return `${start} – ${end}`;
};

const formatCurrency = amount =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount ?? 0);

const formatDate = iso =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      })
    : "—";

const getInitials = (first, last) =>
  `${(first ?? "").charAt(0)}${(last ?? "").charAt(0)}`.toUpperCase();

const STATUS_COLORS = {
  QUALIFIED: "green",
  PENDING: "yellow",
  CANCELLED: "red",
};

function PaySummaryPage() {
  const { getVerifiedJWT } = useAuth();
  const { showSuccess } = useToast();

  const [sellingYears, setSellingYears] = useState([]);
  const [allPayPeriods, setAllPayPeriods] = useState([]);
  const [selectedYearId, setSelectedYearId] = useState(null);
  const [selectedPayPeriodId, setSelectedPayPeriodId] = useState(null);
  const [payPeriodData, setPayPeriodData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const fetchData = useCallback(
    async ({ payPeriodId, yearId } = {}) => {
      try {
        setLoading(true);
        setError(null);
        const jwt = await getVerifiedJWT();
        if (!jwt) return;

        const body = {};
        if (payPeriodId && payPeriodId !== "all")
          body.pay_period_id = Number(payPeriodId);
        if (yearId) body.selling_year_id = Number(yearId);

        const response = await fetch(
          getApiEndpoint("/v1/pyr/pay-period-data"),
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${jwt}`,
            },
            body: JSON.stringify(body),
          }
        );

        if (response.ok) {
          const result = await response.json();
          const data = result?.data ?? result ?? {};

          const years = Array.isArray(data.selling_years)
            ? data.selling_years
            : [];
          const periods = Array.isArray(data.pay_periods)
            ? data.pay_periods
            : [];

          setSellingYears(years);
          setAllPayPeriods(periods);
          setPayPeriodData(data.pay_period_data ?? null);
          setExpandedUserId(null);

          if (!payPeriodId && !yearId) {
            const activeYear = years.find(y => y.is_active) ?? years[0] ?? null;
            setSelectedYearId(activeYear ? String(activeYear.id) : null);
            setSelectedPayPeriodId("all");
          }
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.message || "Failed to load pay period data");
        }
      } catch (err) {
        setError("Failed to load pay period data");
      } finally {
        setLoading(false);
      }
    },
    [getVerifiedJWT]
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleYearChange = value => {
    setSelectedYearId(value);
    setSelectedPayPeriodId("all");
    fetchData({ yearId: value, payPeriodId: "all" });
  };

  const handlePayPeriodChange = value => {
    setSelectedPayPeriodId(value);
    fetchData({ yearId: selectedYearId, payPeriodId: value });
  };

  const toggleUser = userId => {
    setExpandedUserId(prev => (prev === userId ? null : userId));
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v1/pyr/sync-upfront-pay"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const updated = result?.data?.accounts_updated ?? 0;
        showSuccess(`${updated} account${updated === 1 ? "" : "s"} updated`);
        fetchData({ yearId: selectedYearId, payPeriodId: selectedPayPeriodId });
      }
    } catch (err) {
      // silent
    } finally {
      setSyncing(false);
    }
  };

  const visiblePeriods = selectedYearId
    ? allPayPeriods.filter(p => String(p.selling_year_id) === selectedYearId)
    : allPayPeriods;

  const users = Array.isArray(payPeriodData?.users) ? payPeriodData.users : [];

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end" wrap="nowrap">
        <Group gap="sm" align="flex-end" wrap="nowrap">
          <Select
            label="Selling Year"
            placeholder={
              loading && sellingYears.length === 0
                ? "Loading..."
                : "Select selling year"
            }
            data={sellingYears.map(y => ({
              value: String(y.id),
              label: y.name,
            }))}
            value={selectedYearId}
            onChange={handleYearChange}
            disabled={loading && sellingYears.length === 0}
            allowDeselect={false}
            w={220}
          />
          <Select
            label="Pay Period"
            placeholder={loading ? "Loading..." : "Select pay period"}
            data={[
              { value: "all", label: "All Pay Periods" },
              ...visiblePeriods.map(p => ({
                value: String(p.id),
                label: formatDateRange(p.starting_date, p.ending_date),
              })),
            ]}
            value={selectedPayPeriodId}
            onChange={handlePayPeriodChange}
            disabled={loading && visiblePeriods.length === 0}
            allowDeselect={false}
            w={260}
            rightSection={loading ? <Loader size="xs" /> : undefined}
          />
        </Group>
        <Button
          leftSection={<IconRefresh size={16} />}
          variant="light"
          onClick={handleSync}
          loading={syncing}
        >
          Sync
        </Button>
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
          {error}
        </Alert>
      )}

      {!error && (
        <ScrollArea>
          <Table withTableBorder withColumnBorders highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={28} />
                <Table.Th>Rep</Table.Th>
                <Table.Th>Accounts</Table.Th>
                <Table.Th>Revenue</Table.Th>
                <Table.Th>Upfront Paid</Table.Th>
                <Table.Th>Upfront Clawback</Table.Th>
                <Table.Th>Bonus Paid</Table.Th>
                <Table.Th>Total Paid</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading ? (
                <Table.Tr>
                  <Table.Td colSpan={8}>
                    <Group justify="center" py="md">
                      <Loader size="sm" />
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ) : users.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={8}>
                    <Text size="sm" c="dimmed" ta="center" py="md">
                      No data for this pay period.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                users.flatMap(u => {
                  const isExpanded = expandedUserId === u.user_id;
                  const accounts = Array.isArray(u.accounts) ? u.accounts : [];
                  return [
                    <Table.Tr
                      key={u.user_id}
                      onClick={() => toggleUser(u.user_id)}
                      style={{ cursor: "pointer" }}
                    >
                      <Table.Td>
                        {isExpanded ? (
                          <IconChevronDown size={14} />
                        ) : (
                          <IconChevronRight size={14} />
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap="sm" wrap="nowrap">
                          <Avatar
                            src={u.profile_image_url || null}
                            size={32}
                            radius="xl"
                          >
                            {getInitials(u.first_name, u.last_name)}
                          </Avatar>
                          <Text size="sm" fw={500}>
                            {u.first_name} {u.last_name}
                          </Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>{u.qualified_accounts ?? 0}</Table.Td>
                      <Table.Td>{formatCurrency(u.qualified_revenue)}</Table.Td>
                      <Table.Td>
                        {formatCurrency(u.upfront_amount_paid)}
                      </Table.Td>
                      <Table.Td>
                        {formatCurrency(u.upfront_clawback_amount)}
                      </Table.Td>
                      <Table.Td>{formatCurrency(u.bonus_amount_paid)}</Table.Td>
                      <Table.Td fw={600}>
                        {formatCurrency(u.total_amount_paid)}
                      </Table.Td>
                    </Table.Tr>,
                    isExpanded && (
                      <Table.Tr key={`${u.user_id}-accounts`}>
                        <Table.Td colSpan={8} p={0}>
                          <Box bg="gray.0" px="md" py="sm">
                            {accounts.length === 0 ? (
                              <Text size="sm" c="dimmed">
                                No accounts.
                              </Text>
                            ) : (
                              <Table withTableBorder withColumnBorders>
                                <Table.Thead>
                                  <Table.Tr>
                                    <Table.Th>Service</Table.Th>
                                    <Table.Th>Office</Table.Th>
                                    <Table.Th>Date Sold</Table.Th>
                                    <Table.Th>Initial Price</Table.Th>
                                    <Table.Th>Annual Value</Table.Th>
                                    <Table.Th>Status</Table.Th>
                                  </Table.Tr>
                                </Table.Thead>
                                <Table.Tbody>
                                  {accounts.map(a => (
                                    <Table.Tr key={a.id}>
                                      <Table.Td>
                                        <Text size="sm">
                                          {a.subscription?.service_type ?? "—"}
                                        </Text>
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size="sm">
                                          {a.subscription?.office_name ?? "—"}
                                        </Text>
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size="sm">
                                          {formatDate(
                                            a.subscription?.date_added
                                          )}
                                        </Text>
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size="sm">
                                          {formatCurrency(
                                            a.subscription?.initial_price
                                          )}
                                        </Text>
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size="sm">
                                          {formatCurrency(
                                            a.subscription?.annual_value
                                          )}
                                        </Text>
                                      </Table.Td>
                                      <Table.Td>
                                        <Badge
                                          color={
                                            STATUS_COLORS[a.account_status] ??
                                            "gray"
                                          }
                                          variant="light"
                                          size="sm"
                                        >
                                          {a.account_status ?? "—"}
                                        </Badge>
                                      </Table.Td>
                                    </Table.Tr>
                                  ))}
                                </Table.Tbody>
                              </Table>
                            )}
                          </Box>
                        </Table.Td>
                      </Table.Tr>
                    ),
                  ].filter(Boolean);
                })
              )}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      )}
    </Stack>
  );
}

export default PaySummaryPage;
